import { z } from 'zod';
import { Condition, evaluate, isAnswered } from './conditions.js';
/**
 * Workflows (tracker task 42, decision 0025): a graph of steps a stage or a rule starts on an assessment,
 * such as ask for a policy, have an underwriter review it, or create a task for the business. The
 * definition is configuration; `advance` is the whole runtime, pure: given where a run is and what is
 * known now, it moves through every step that can complete and stops at the first one that has to wait.
 * The core stores runs, carries out the actions and calls `advance` again when something changes.
 *
 * Steps (the prototype's node types that can run today):
 *   start · question · condition · document_request · validation · human_review · create_task ·
 *   notification · sub_workflow · wait · end
 */
const NodeId = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_.-]{0,63}$/, 'letters, digits, _ . - (max 64)');
const Ref = z.string().min(1).max(64);
const Next = NodeId;
const Label = z.string().max(200).default('');
/** Who a task or a review is for: a role in the tenant. */
export const TASK_ROLES = ['sme_owner', 'sme_member', 'broker', 'underwriter', 'tenant_admin'];
const Role = z.enum(TASK_ROLES);
const node = (type, shape) => z.strictObject({ id: NodeId, type: z.literal(type), label: Label, ...shape });
export const WorkflowNode = z.discriminatedUnion('type', [
    node('start', { next: Next }),
    /** Waits until the question is answered. A question hidden by the person's other answers is passed. */
    node('question', { question: Ref, next: Next }),
    /** Branches on a condition over answers and facts. */
    node('condition', { when: Condition, then: Next, else: Next }),
    /** Asks for a document type; waits until one is stored and classified as that type, unless optional. */
    node('document_request', { document_type: Ref, optional: z.boolean().default(false), next: Next }),
    /** A deterministic check: `pass` if it holds, `fail` otherwise (e.g. to a review or a task). */
    node('validation', { check: Condition, pass: Next, fail: Next }),
    /** Holds for a person in `role` to approve or reject. */
    node('human_review', {
        title: z.string().trim().min(1).max(200),
        role: Role.default('underwriter'),
        approved: Next,
        rejected: Next,
    }),
    /** Adds a task for the business or staff and carries on. */
    node('create_task', {
        title: z.string().trim().min(1).max(200),
        description: z.string().max(1000).default(''),
        role: Role.default('sme_owner'),
        due_days: z.number().int().min(1).max(730).optional(),
        next: Next,
    }),
    /** Announces something (`workflow.notification`); delivering it is a notification channel's job. */
    node('notification', { template: Ref, to: z.array(Role).min(1), next: Next }),
    /** Runs another workflow on the same assessment and waits for it to finish. */
    node('sub_workflow', { workflow: Ref, next: Next }),
    /** Waits a number of days from when it is reached. */
    node('wait', { days: z.number().int().min(1).max(730), next: Next }),
    node('end', { outcome: z.string().max(100).default('completed') }),
]);
/** Where each kind of step can go next, for checks and the builder. */
export function successors(n) {
    switch (n.type) {
        case 'condition':
            return [n.then, n.else];
        case 'validation':
            return [n.pass, n.fail];
        case 'human_review':
            return [n.approved, n.rejected];
        case 'end':
            return [];
        default:
            return [n.next];
    }
}
/** Every problem with a graph: ids, links, one start, nothing unreachable, no loops, every path ends. */
export function graphProblems(nodes) {
    const out = [];
    const byId = new Map();
    nodes.forEach((n, i) => {
        if (byId.has(n.id))
            out.push({ path: `nodes.${i}.id`, message: `step id ${n.id} is used twice` });
        byId.set(n.id, n);
    });
    const starts = nodes.filter((n) => n.type === 'start');
    if (starts.length !== 1)
        out.push({ path: 'nodes', message: `needs exactly one start step (has ${starts.length})` });
    if (!nodes.some((n) => n.type === 'end'))
        out.push({ path: 'nodes', message: 'needs an end step' });
    nodes.forEach((n, i) => successors(n).forEach((s) => {
        if (!byId.has(s))
            out.push({ path: `nodes.${i}`, message: `${n.id} goes to ${s}, which does not exist` });
        if (s === n.id)
            out.push({ path: `nodes.${i}`, message: `${n.id} goes to itself` });
    }));
    if (out.length || !starts[0])
        return out;
    // Reachable from the start, and no loops (a run must always move forward).
    const seen = new Set();
    const onPath = new Set();
    const visit = (id, trail) => {
        if (onPath.has(id)) {
            out.push({
                path: 'nodes',
                message: `steps loop: ${[...trail.slice(trail.indexOf(id)), id].join(' → ')}`,
            });
            return;
        }
        if (seen.has(id))
            return;
        seen.add(id);
        onPath.add(id);
        for (const s of successors(byId.get(id)))
            visit(s, [...trail, id]);
        onPath.delete(id);
    };
    visit(starts[0].id, []);
    nodes.forEach((n, i) => {
        if (!seen.has(n.id))
            out.push({ path: `nodes.${i}`, message: `${n.id} can never be reached from the start` });
    });
    return out;
}
export const WorkflowDefinition = z
    .strictObject({
    name: z.string().trim().min(1).max(200),
    description: z.string().max(1000).default(''),
    nodes: z.array(WorkflowNode).min(2).max(200),
})
    .superRefine((w, ctx) => {
    for (const p of graphProblems(w.nodes))
        ctx.addIssue({ code: 'custom', path: p.path.split('.').map((x) => (/^\d+$/.test(x) ? Number(x) : x)), message: p.message }); // prettier-ignore
});
/** What a workflow refers to by key, for set-wide validation. */
export function workflowRefs(w) {
    const refs = { questions: [], document_types: [], workflows: [] };
    const cond = (c) => {
        if ('all' in c)
            c.all.forEach(cond);
        else if ('any' in c)
            c.any.forEach(cond);
        else if ('not' in c)
            cond(c.not);
        else if ('question' in c)
            refs.questions.push(c.question);
    };
    for (const n of w.nodes) {
        if (n.type === 'question')
            refs.questions.push(n.question);
        if (n.type === 'condition')
            cond(n.when);
        if (n.type === 'validation')
            cond(n.check);
        if (n.type === 'document_request')
            refs.document_types.push(n.document_type);
        if (n.type === 'sub_workflow')
            refs.workflows.push(n.workflow);
    }
    return refs;
}
// ---------- runtime ----------
export const RUN_STATUSES = ['running', 'waiting', 'completed', 'cancelled', 'failed'];
export function startState(today) {
    return { status: 'running', at: null, reached_on: today, waiting: null, steps: [], outcome: null };
}
const addDays = (iso, days) => {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
};
/**
 * Moves a run forward as far as it can. Deterministic: the same definition, state and context give the
 * same result, so running it twice (a retried job) changes nothing the second time.
 */
export function advance(def, state, ctx) {
    if (state.status === 'completed' || state.status === 'cancelled' || state.status === 'failed')
        return { state, actions: [] };
    const byId = new Map(def.nodes.map((n) => [n.id, n]));
    const s = { ...state, steps: [...state.steps] };
    const actions = [];
    if (s.at === null && !s.steps.length)
        s.at = def.nodes.find((n) => n.type === 'start').id;
    const step = (n, outcome, next) => {
        s.steps.push({ node: n.id, type: n.type, outcome, on: ctx.today });
        s.at = next;
        s.reached_on = ctx.today;
        s.waiting = null;
    };
    const wait = (w) => {
        s.status = 'waiting';
        s.waiting = w;
    };
    // A graph has no loops (checked on save), so at most one visit per step.
    for (let guard = 0; guard <= def.nodes.length; guard++) {
        const n = s.at ? byId.get(s.at) : undefined;
        if (!n) {
            s.status = 'failed';
            s.outcome = `step ${s.at} is not in this version of the workflow`;
            s.at = null;
            return { state: s, actions };
        }
        s.status = 'running';
        // A step reached for the first time does its one-off action before (maybe) waiting.
        const first = s.waiting === null;
        switch (n.type) {
            case 'start':
                step(n, 'started', n.next);
                break;
            case 'question':
                if (ctx.hidden.has(n.question))
                    step(n, 'not asked', n.next);
                else if (isAnswered(ctx.answers[n.question]))
                    step(n, 'answered', n.next);
                else
                    return (wait({ kind: 'answer', question: n.question }), { state: s, actions });
                break;
            case 'condition': {
                const yes = evaluate(n.when, ctx);
                step(n, yes ? 'then' : 'else', yes ? n.then : n.else);
                break;
            }
            case 'validation': {
                const ok = evaluate(n.check, ctx);
                step(n, ok ? 'pass' : 'fail', ok ? n.pass : n.fail);
                break;
            }
            case 'document_request':
                if (first)
                    actions.push({ type: 'request_document', node: n.id, document_type: n.document_type, optional: n.optional }); // prettier-ignore
                if (ctx.documentTypes.has(n.document_type))
                    step(n, 'received', n.next);
                else if (n.optional)
                    step(n, 'requested', n.next);
                else
                    return (wait({ kind: 'document', document_type: n.document_type }), { state: s, actions });
                break;
            case 'human_review': {
                if (first)
                    actions.push({ type: 'create_task', node: n.id, kind: 'review', title: n.title, description: n.label, role: n.role, due_on: null }); // prettier-ignore
                const d = ctx.reviews[n.id];
                if (d)
                    step(n, d, d === 'approved' ? n.approved : n.rejected);
                else
                    return (wait({ kind: 'review', task: n.id }), { state: s, actions });
                break;
            }
            case 'create_task':
                actions.push({ type: 'create_task', node: n.id, kind: 'task', title: n.title, description: n.description, role: n.role, due_on: n.due_days ? addDays(ctx.today, n.due_days) : null }); // prettier-ignore
                step(n, 'task created', n.next);
                break;
            case 'notification':
                actions.push({ type: 'notify', node: n.id, template: n.template, to: [...n.to] });
                step(n, 'sent', n.next);
                break;
            case 'sub_workflow': {
                if (first)
                    actions.push({ type: 'start_workflow', node: n.id, workflow: n.workflow });
                const child = ctx.children[n.workflow];
                if (child === 'completed')
                    step(n, 'completed', n.next);
                else if (child === 'cancelled' || child === 'failed')
                    step(n, child, n.next);
                else
                    return (wait({ kind: 'workflow', workflow: n.workflow }), { state: s, actions });
                break;
            }
            case 'wait': {
                const until = addDays(s.reached_on, n.days);
                if (ctx.today >= until)
                    step(n, 'waited', n.next);
                else
                    return (wait({ kind: 'date', until }), { state: s, actions });
                break;
            }
            case 'end':
                s.steps.push({ node: n.id, type: n.type, outcome: n.outcome, on: ctx.today });
                s.status = 'completed';
                s.outcome = n.outcome;
                s.at = null;
                s.waiting = null;
                return { state: s, actions };
        }
    }
    s.status = 'failed';
    s.outcome = 'the workflow did not finish moving (a loop?)';
    return { state: s, actions };
}
//# sourceMappingURL=workflows.js.map